import { PACKAGES } from '@/lib/realEstatePackages';
import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, User, LogIn, ArrowRight, Mail } from 'lucide-react';
import { Button } from "@/components/ui/button";
import SEO from '../../components/SEO';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * ORDER SUCCESS PAGE - Post-payment confirmation
 * Route: /order/success
 * 
 * Displays after Stripe payment redirect, encourages account creation.
 * Reads optional ?pkg= query param for package context.
 * 
 * STRIPE REDIRECT SETUP (for admin):
 * In Stripe Payment Links, set "After payment → Redirect to URL":
 *   https://<domain>/order/success?pkg=signature
 *   https://<domain>/order/success?pkg=first-impressions
 *   https://<domain>/order/success?pkg=creator-spotlight
 * ═══════════════════════════════════════════════════════════════════════════
 */

const OrderSuccess = () => {
  const [searchParams] = useSearchParams();
  const pkg = searchParams.get('pkg');

  // Package display names
  const packageNames = {
    'signature': PACKAGES.signature.name,
    'first-impressions': PACKAGES.first_impressions.name,
    'creator-spotlight': PACKAGES.creator_spotlight.name,
  };

  const packageName = pkg ? packageNames[pkg] : null;

  return (
    <div className="min-h-screen bg-[#050816] flex flex-col">
      {/* SEO - NoIndex for transactional page */}
      <SEO
        title="Payment Confirmed"
        description="Thank you for your order with RC Studios. Create an account to manage your project."
        path="/order/success"
        noIndex={true}
      />

      {/* Main Content */}
      <main className="flex-grow flex items-center justify-center px-6 py-16 md:py-24">
        <div className="max-w-lg w-full text-center">
          {/* Success Icon */}
          <div className="mb-8 flex justify-center">
            <div className="w-20 h-20 rounded-full bg-[#F5C056]/10 flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-[#F5C056]" />
            </div>
          </div>

          {/* Title */}
          <h1 className="font-serif text-4xl md:text-5xl text-white mb-4">
            Payment Confirmed
          </h1>

          {/* Package Info (if available) */}
          {packageName && (
            <div className="mb-6">
              <span className="inline-block px-4 py-2 rounded-full bg-white/5 border border-white/10 text-[#F5C056] text-sm font-medium">
                Package: {packageName}
              </span>
            </div>
          )}

          {/* Subtext */}
          <p className="text-lg text-gray-300 mb-10 leading-relaxed">
            Thank you — your order is secured. Next, create an account to manage uploads, delivery, and revisions.
          </p>

          {/* CTA Buttons */}
          <div className="space-y-4">
            {/* Primary: Create Account */}
            <Button
              asChild
              className="w-full h-14 bg-[#F5C056] text-[#050816] hover:bg-[#F5C056]/90 rounded-xl text-base font-semibold shadow-lg shadow-[#F5C056]/20 hover:-translate-y-0.5 transition-all duration-200"
              data-testid="success-create-account-btn"
            >
              <Link to="/register" className="flex items-center justify-center gap-3">
                <User className="w-5 h-5" />
                Create Account
                <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>

            {/* Secondary: Sign In */}
            <Button
              asChild
              variant="outline"
              className="w-full h-14 bg-transparent border-white/20 text-white hover:bg-white/5 hover:border-white/30 rounded-xl text-base font-medium transition-all duration-200"
              data-testid="success-signin-btn"
            >
              <Link to="/login" className="flex items-center justify-center gap-3">
                <LogIn className="w-5 h-5" />
                Sign In
              </Link>
            </Button>

            {/* Tertiary: Continue as Guest */}
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 text-gray-400 hover:text-white text-sm transition-colors mt-4"
              data-testid="success-guest-btn"
            >
              Continue — Send Project Details
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          {/* Email Receipt Note */}
          <div className="mt-12 pt-8 border-t border-white/10">
            <div className="flex items-center justify-center gap-2 text-gray-500 text-sm">
              <Mail className="w-4 h-4" />
              <span>You'll also receive an email receipt from Stripe.</span>
            </div>
          </div>

          {/* What's Next Section */}
          <div className="mt-10 p-6 rounded-2xl bg-white/[0.03] border border-white/10 text-left">
            <h3 className="text-white font-medium mb-4">What happens next?</h3>
            <ol className="space-y-3 text-sm text-gray-400">
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#F5C056]/10 text-[#F5C056] flex items-center justify-center text-xs font-bold">1</span>
                <span>We'll reach out within 24 hours to schedule your session</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#F5C056]/10 text-[#F5C056] flex items-center justify-center text-xs font-bold">2</span>
                <span>Create an account to upload property details and access</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#F5C056]/10 text-[#F5C056] flex items-center justify-center text-xs font-bold">3</span>
                <span>Receive your edited media within 24 hours of the shoot</span>
              </li>
            </ol>
          </div>
        </div>
      </main>

      {/* Footer Note */}
      <footer className="py-6 text-center">
        <p className="text-gray-600 text-sm">
          Questions? Contact us at{' '}
          <a href="mailto:marketing@rcstudios.net" className="text-[#F5C056] hover:text-[#F5C056]/80 transition-colors">
            marketing@rcstudios.net
          </a>
        </p>
      </footer>
    </div>
  );
};

export default OrderSuccess;
